import { useEffect, useState, type ClipboardEvent, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { LoginBackdrop } from '@/components/login/LoginBackdrop';
import { LoginCard } from '@/components/login/LoginCard';
import { Notice } from '@/components/Modal';
import { forgetPassword, recallPassword, rememberPassword as sealPassword } from '@/lib/auth/passwordVault';
import { useLogin, useSession } from '@/lib/auth/session';
import type { DeviceChallenge } from '@/lib/types';
import { DeviceVerifyModal } from '@/pages/login/DeviceVerifyModal';
import { PasswordResetModal } from '@/pages/login/PasswordResetModal';
import {
  LOGIN_DESIGN_OFF,
  backdropDark,
  sceneOnLeft,
  useDesignArt,
  useDesignArtPick,
  useLoginDesign,
} from '@/lib/login/loginDesign';
import { useStackedLayout } from '@/lib/utils/useIsMobile';

/**
 * 로그인 화면. v1 `components/LoginClient.tsx` 이식.
 *
 * **"비밀번호 저장" 은 v1 의 편의를 그대로 가져오되 저장 방식만 바꿨다.** v1 은 평문을
 * localStorage 에 넣었고, v2 는 봉인해서 넣는다([[passwordVault]] — 열쇠는 IndexedDB 의
 * 추출 불가 CryptoKey). 사용자가 보는 동작은 같다: 로그인하면 다음 방문에 두 칸이 채워진 채로
 * 열리고, 체크를 풀면 그 즉시 저장값이 사라진다.
 *
 * **체크는 기본이 켜짐이다.** 사내 PC 전용이라는 전제(passwordVault 주석) 위에서 저장이 기본 동작이고,
 * 원하지 않는 사람은 끄면 된다. 꺼짐이 기본이면 저장값을 불러오지 못한 날 — 브라우저가 사이트 데이터를
 * 비웠거나 봉인이 열리지 않은 날 — 체크가 조용히 풀린 채로 열려, 그 뒤로는 매번 입력하게 된다.
 *
 * 비밀번호 칸은 복사도 막는다 — 어깨너머로 보거나 잠깐 자리를 비운 사이 값을 퍼가는 것까지가
 * 이 화면이 막을 수 있는 범위다. XSS 는 두 장치 모두 막지 못한다(자세한 이유는 passwordVault 주석).
 *
 * <h2>왼쪽 절반은 히어로다 — 켜 두었다면</h2>
 * 이 화면은 <b>하루 한 번 열고 바로 떠나는</b> 자리라 무거운 그림을 둘 수 있는 거의 유일한
 * 화면이다(히어로는 매 프레임 유체 시뮬 + GPGPU 입자 두 판 + bloom 을 돌린다). 로그인에
 * 성공하면 `window.location.assign` 으로 앱을 새로 띄우므로 씬도 확실히 죽는다 — 비용이 이
 * 화면 안에 갇힌다.
 *
 * <p>켜고 끄는 것은 <b>환경설정 › 로그인 화면</b>의 스위치다(사용자 지시 2026-09-22). 씬과 색을
 * 들고 있는 것은 바탕 조각이고({@link LoginBackdrop} · {@code LoginHeroScene}), 이 화면이 하는
 * 일은 <b>카드를 오른쪽으로 물리는</b> 것뿐이다 — 구체가 왼쪽 절반에 서므로 카드가 가운데에
 * 있으면 둘이 겹친다.
 *
 * <p>두 가지는 그래도 지킨다.
 * <ul>
 *   <li><b>좁은 화면에서는 통째로 뺀다.</b> 로그인은 폰으로도 하는데, 거기서 지불할 값이
 *       아니다 — 아래 {@code stacked} 가 시안 전부를 끈다. 그때는 카드 한 장이 가운데 선다.</li>
 *   <li><b>번들에 싣지 않는다.</b> three 는 크고, 로그인은 앱에서 가장 먼저 뜨는 화면이다.
 *       바탕 조각이 {@code lazy} 로 감싸 <b>실제로 그릴 때만</b> 받아온다.</li>
 * </ul>
 */

/** 시안도 히어로도 서지 않을 때의 바탕. 밝은 회색 한 겹 위에 흰 카드가 뜬다. */
const PAGE_BACKDROP = '#f1f5f9';

/**
 * 시안이 설 때의 바탕(환경설정 › 로그인 화면).
 *
 * <p>캔버스가 오기 전의 색이다. 밝은 회색을 그대로 두면 사진이 올라오는 순간 <b>흰 판이 한 번
 * 번쩍인다</b>. 히어로도 같은 이유로 제 배경색을 스스로 미리 깐다({@code heroBackdropCss}) —
 * 그쪽은 색이 설정값이라 이 검정 위에 제 그라디언트를 한 겹 더 덮는다.
 */
const SHOW_BACKDROP = '#000000';

const LOGIN_EMAIL_KEY = 'bomi:login-email';

/** 복사를 눌렀을 때 클립보드에 대신 들어가는 값. 길이도 알려주지 않도록 고정 길이로 둔다. */
const CLIPBOARD_DECOY = '••••••••';

/** 돌아갈 곳을 못 정했을 때의 기본 도착지. */
const HOME_PATH = '/home';

/** 열린 리다이렉트를 막는다. `//evil.com` 같은 값은 스킴 없는 절대 URL 이므로 함께 걸러낸다. */
function getSafeReturnPath(returnTo: string | null): string {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return HOME_PATH;
  return returnTo;
}

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const { data: session } = useSession();
  const login = useLogin();

  /*
    화면 겉모습(환경설정 › 로그인 화면). <b>기다리지 않는다</b> — 카드가 먼저 서고 바탕만 나중에
    올라온다. 이 조회가 늦거나 실패해도 로그인은 그대로 되어야 한다.

    <p>좁은 화면에서는 아예 켜지 않는다. 로그인은 폰으로도 하는데, 사진 여러 장과 WebGL 한 판은
    거기서 지불할 값이 아니다(히어로를 `lg` 미만에서 뺐던 것과 같은 판단). 커서 꼬리는 더 그렇다 —
    가리킬 커서가 없다.
  */
  const stacked = useStackedLayout();
  const { data: designData } = useLoginDesign();
  /*
    「디자인아트」(카드 머리의 스위치 · 사용자 지시 2026-10-08). 끈 사람(기본)은 바탕 없이 카드만, 켠 사람은
    관리자 설정에서 켜 둔 장면(단지 · 수영장 · 세계 지도 · 야경 · 수족관) 중 하나가 들어올 때마다 무작위로 선다
    (사용자 지적 2026-10-20 — 끈 세계 지도가 나왔다). 좁은 화면(`stacked`)에서는 켜도
    서지 않는다 — 위 주석과 같은 이유다.
  */
  const [designArt, setDesignArt] = useDesignArt();
  /** 관리자가 환경설정 › 로그인 화면에서 켜고 저장해야 스위치가 생긴다. 꺼져 있으면 스위치도 장면도 없다. */
  const artAllowed = designData?.designArtEnabled === true;
  const artPick = useDesignArtPick(artAllowed && designArt, designData);
  const pick = stacked ? null : artPick;
  const design = pick === null ? LOGIN_DESIGN_OFF : (designData ?? LOGIN_DESIGN_OFF);
  const showing = backdropDark(design, pick);
  /** 장면(히어로 · 단지 · 수영장)이 왼쪽 절반을 쓰는 동안에는 카드가 오른쪽으로 물러난다. */
  const hero = sceneOnLeft(pick);

  const nextPath = getSafeReturnPath(searchParams.get('returnTo'));

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberEmail, setRememberEmail] = useState(true);
  // 기본값이 켜짐이다. 꺼짐이면 저장값을 불러오지 못한 날(저장소가 비워졌거나 봉인이 열리지 않은 날)
  // 체크가 조용히 풀린 채로 열려, 사용자는 다시 켜지 않는 한 그날 이후로 계속 입력하게 된다.
  const [rememberPassword, setRememberPassword] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  /** 새 브라우저 — 비밀번호는 맞았고 인증번호(휴대폰 · 메일) 한 걸음이 남았다(DOC-138). 서버 답이 이 값을 준다. */
  const [deviceChallenge, setDeviceChallenge] = useState<DeviceChallenge | null>(null);

  useEffect(() => {
    const savedEmail = window.localStorage.getItem(LOGIN_EMAIL_KEY);
    if (savedEmail) setEmail(savedEmail);

    // 봉인을 여는 데 IndexedDB 왕복이 필요해 한 박자 늦게 채워진다. 그 사이 사용자가 직접 입력했다면
    // 그쪽이 우선이다 — 타이핑하던 글자를 저장값이 덮어쓰지 않게 한다.
    let cancelled = false;
    void recallPassword().then((saved) => {
      if (cancelled || !saved) return;
      setPassword((current) => (current === '' ? saved : current));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
    이미 로그인돼 있으면 원래 가려던 곳으로 보낸다.

    <b>「로그인이 보낸 것」이라는 표시를 함께 싣는다</b>(사용자 지시 2026-09-14). 돌아갈 곳이
    권한 없는 화면이면 {@code RequireAuth} 가 다시 홈으로 되돌리는데, 그때 <b>사유 띠를 띄우지
    않기</b> 위해서다 — 어제 보던 화면으로 돌아가려다 막힌 것은 사용자가 한 일이 아니고, 아무것도
    누르지 않았는데 「권한이 없습니다」 를 보게 된다.
  */
  if (session?.authenticated) {
    return <Navigate to={nextPath} replace state={{ fromLogin: true }} />;
  }

  /** 세션이 열린 뒤의 마무리 — 로그인과 새 기기 인증이 같은 손으로 끝낸다. */
  const finishLogin = async () => {
    if (rememberEmail) {
      window.localStorage.setItem(LOGIN_EMAIL_KEY, email.trim());
    } else {
      window.localStorage.removeItem(LOGIN_EMAIL_KEY);
    }
    // 로그인에 성공한 뒤에만 저장한다 — 틀린 비밀번호를 기억해 두면 다음 방문이 더 나빠진다.
    // 봉인은 비동기라 반드시 기다린다. 먼저 이동해 버리면 저장이 끝나기 전에 화면이 갈아엎힌다.
    if (rememberPassword) await sealPassword(password);
    else forgetPassword();

    // 세션 쿠키가 붙은 상태로 앱을 새로 띄운다. 이전 사용자의 쿼리 캐시가 남지 않는다.
    window.location.assign(nextPath);
  };

  const submitLogin = () => {
    if (login.isPending) return;

    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: async (session) => {
          /*
            처음 보는 브라우저 — 서버가 세션 대신 인증 갈래를 묻는 답을 줬다(DOC-138). 비밀번호는 맞았으니
            여기서 멈추고 모달이 번호를 받아 마무리한다.
          */
          if (session.deviceChallenge) {
            setDeviceChallenge(session.deviceChallenge);
            return;
          }
          await finishLogin();
        },
      },
    );
  };

  const handleLogin = (event: FormEvent) => {
    event.preventDefault();
    submitLogin();
  };

  /**
   * 비밀번호 칸에서의 복사·잘라내기. 원문 대신 가짜 값이 클립보드에 들어간다.
   * 잘라내기는 지우는 동작까지 함께 막는다 — 값이 사라지면 막혔다는 것도 모른 채 다시 입력하게 된다.
   */
  const maskClipboard = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.clipboardData.setData('text/plain', CLIPBOARD_DECOY);
  };

  return (
    <>
      <div className="relative min-h-screen" style={{ background: showing ? SHOW_BACKDROP : PAGE_BACKDROP }}>
        {/* 시안은 <b>화면 전체</b>에 깔린다(사용자 결정 2026-09-15). 왼쪽 절반에만 두면 오른쪽과
            색이 갈려 한가운데에 세로 경계선이 생긴다 — 아래 히어로 주석이 같은 이유를 적어 뒀다. */}
        <LoginBackdrop design={design} pick={pick} />

        {/* 히어로가 서면 카드가 오른쪽 절반으로 물러난다 — 구체는 왼쪽 절반의 한가운데에 선다. */}
        <div
          className="relative flex min-h-screen items-center justify-center"
          /* 커서 꼬리가 `z-index:2` 로 서므로 카드는 그보다 위여야 한다 — 아니면 알갱이가
             아이디 칸 위를 지나간다(누를 수는 있다. 그 층은 손가락을 받지 않는다). */
          style={{
            padding: '24px',
            zIndex: 3,
            width: hero ? '50%' : '100%',
            marginLeft: hero ? 'auto' : undefined,
            /* 장면(히어로 · 단지 · 수영장)은 커서를 따라간다. 오른쪽 절반을 이 칸이 덮고 있으면 거기서는
               단지가 솟지 않고 물이 따라오지 않는다 — 칸은 흘려보내고 카드만 받는다(아래 `contents` 래퍼). */
            pointerEvents: hero ? 'none' : undefined,
          }}
        >
          {/* 바탕 위에 «흰 카드 한 장». 흰 것은 여기까지다.
              생김새는 {@link LoginCard} 가 들고 있다 — 환경설정의 미리보기가 <b>같은 조각</b>을
              세우기 때문이다(모형을 따로 그리면 칸이 하나 늘 때 미리보기만 옛 모습으로 남는다). */}
          {/* 칸이 손가락을 흘려보내도 카드는 받는다. `contents` 라 상자가 생기지 않아 자리는 그대로다. */}
          <div style={{ display: 'contents', pointerEvents: 'auto' }}>
          <LoginCard
            designArt={designArt}
            onDesignArtChange={artAllowed ? setDesignArt : undefined}
            elevated={showing}
            email={email}
            onEmailChange={setEmail}
            password={password}
            onPasswordChange={setPassword}
            onPasswordClipboard={maskClipboard}
            rememberEmail={rememberEmail}
            onRememberEmailChange={setRememberEmail}
            rememberPassword={rememberPassword}
            onRememberPasswordChange={(next) => {
              setRememberPassword(next);
              // 로그인까지 가지 않고 창을 닫아도 남지 않도록, 해제는 저장 시점을 기다리지 않는다.
              if (!next) forgetPassword();
            }}
            pending={login.isPending}
            onSubmit={handleLogin}
            onResetRequest={() => {
              setResetDone(false);
              setShowResetModal(true);
            }}
            /* 할 말이 없으면 아예 넘기지 않는다 — 빈 조각을 주면 카드 밑에 빈 줄만 한 칸 생긴다. */
            notices={
              login.isError || resetDone ? (
                <>
                  {/* 권한 거부 안내는 홈이 맡는다. 이미 로그인한 사람을 로그인 화면에 세워 둘 이유가 없다. */}
                  {login.isError && <Notice tone="error">{login.error.message}</Notice>}
                  {resetDone && <Notice>비밀번호를 변경했습니다. 새 비밀번호로 로그인해 주세요.</Notice>}
                </>
              ) : undefined
            }
          />
          </div>
        </div>
      </div>

      {deviceChallenge && (
        <DeviceVerifyModal
          challenge={deviceChallenge}
          onVerified={finishLogin}
          onClose={() => setDeviceChallenge(null)}
        />
      )}

      {showResetModal && (
        <PasswordResetModal
          onClose={() => setShowResetModal(false)}
          onDone={() => {
            setShowResetModal(false);
            setPassword('');
            setResetDone(true);
          }}
        />
      )}
    </>
  );
}
